import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Building2, Plus, Trash2 } from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { financeService } from "@/services/finance.service";
import { formatCNPJ, formatPhone } from "@/lib/utils";

const emptyForm = {
    name: "",
    tax_id: "",
    contact_name: "",
    phone: "",
    email: "",
    address: "",
    notes: "",
};

export default function SuppliersListPage() {
    const qc = useQueryClient();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [deletingId, setDeletingId] = useState(null);
    const [error, setError] = useState("");

    const list = useQuery({
        queryKey: ["suppliers", { page, search }],
        queryFn: () => financeService.listSuppliers({
            page, pageSize: 20, search,
            sortBy: "created_at", sortOrder: "desc",
        }),
    });

    const createMut = useMutation({
        mutationFn: (payload) => financeService.createSupplier(payload),
        onSuccess: () => {
            toast.success("Fornecedor cadastrado.");
            qc.invalidateQueries({ queryKey: ["suppliers"] });
            qc.invalidateQueries({ queryKey: ["suppliers-lite-finance"] });
            setDialogOpen(false);
            setForm(emptyForm);
            setError("");
        },
        onError: (e) => setError(e?.response?.data?.detail || e?.message || "Falha ao cadastrar."),
    });

    const deleteMut = useMutation({
        mutationFn: (id) => financeService.deleteSupplier(id),
        onSuccess: () => {
            toast.success("Fornecedor removido.");
            qc.invalidateQueries({ queryKey: ["suppliers"] });
            qc.invalidateQueries({ queryKey: ["suppliers-lite-finance"] });
            setDeletingId(null);
        },
        onError: (e) => toast.error(e?.response?.data?.detail || e?.message || "Falha ao remover."),
    });

    const columns = [
        {
            key: "name",
            header: "Nome",
            cell: (r) => (
                <div className="flex items-center gap-2">
                    <span className="grid h-8 w-8 place-items-center rounded-md bg-muted text-muted-foreground">
                        <Building2 className="h-4 w-4" />
                    </span>
                    <div>
                        <div className="text-sm font-medium">{r.name}</div>
                        {r.contact_name && (
                            <div className="text-xs text-muted-foreground">
                                Contato: {r.contact_name}
                            </div>
                        )}
                    </div>
                </div>
            ),
        },
        { key: "tax_id", header: "CNPJ/CPF", cell: (r) => formatCNPJ(r.tax_id) || r.tax_id || "—" },
        { key: "phone", header: "Telefone", cell: (r) => formatPhone(r.phone) || r.phone || "—" },
        { key: "email", header: "Email", cell: (r) => r.email || "—" },
        {
            key: "actions",
            header: "",
            cell: (r) => (
                <div className="flex justify-end">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setDeletingId(r.id); }}
                        data-testid={`supplier-delete-${r.id}`}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            ),
        },
    ];

    function handleSubmit(e) {
        e?.preventDefault();
        setError("");
        if (!form.name.trim()) {
            setError("O nome é obrigatório.");
            return;
        }
        createMut.mutate(form);
    }

    return (
        <div className="space-y-4" data-testid="suppliers-list">
            <PageHeader
                title="Fornecedores"
                description="Cadastre fornecedores para vincular a contas a pagar."
                actions={
                    <Button
                        onClick={() => { setForm(emptyForm); setError(""); setDialogOpen(true); }}
                        data-testid="suppliers-create-button"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Novo fornecedor
                    </Button>
                }
            />
            <Card>
                <DataTable
                    columns={columns}
                    query={list.data}
                    loading={list.isLoading}
                    onSearch={(v) => { setSearch(v); setPage(1); }}
                    onPageChange={setPage}
                    dataTestId="suppliers-datatable"
                    emptyState={{
                        title: "Nenhum fornecedor cadastrado",
                        description: "Clique em 'Novo fornecedor' para começar.",
                    }}
                />
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="supplier-dialog">
                    <DialogHeader>
                        <DialogTitle>Novo fornecedor</DialogTitle>
                        <DialogDescription>
                            Dados básicos para vincular a contas a pagar.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="s-name">Nome</Label>
                            <Input
                                id="s-name"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                data-testid="supplier-name-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="s-tax">CNPJ/CPF</Label>
                                <Input
                                    id="s-tax"
                                    value={form.tax_id}
                                    onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="s-phone">Telefone</Label>
                                <Input
                                    id="s-phone"
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="s-contact">Contato</Label>
                                <Input
                                    id="s-contact"
                                    value={form.contact_name}
                                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="s-email">Email</Label>
                                <Input
                                    id="s-email"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="s-addr">Endereço</Label>
                            <Input
                                id="s-addr"
                                value={form.address}
                                onChange={(e) => setForm({ ...form, address: e.target.value })}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="s-notes">Observações</Label>
                            <Textarea
                                id="s-notes"
                                rows={2}
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            />
                        </div>
                        {error && (
                            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
                                {error}
                            </div>
                        )}
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setDialogOpen(false)}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                disabled={createMut.isPending}
                                data-testid="supplier-submit-button"
                            >
                                {createMut.isPending ? "Salvando..." : "Cadastrar"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deletingId} onOpenChange={(v) => !v && setDeletingId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remover fornecedor?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Esta ação não pode ser desfeita. Contas a pagar já vinculadas manterão a referência histórica.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deleteMut.mutate(deletingId)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="supplier-delete-confirm"
                        >
                            Remover
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
